import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';



import { ReactComponent as Checkmark } from '@asd-stan/ui-kit/assets/icons/checkmark.svg';
import { ReactComponent as CloseIcon } from '@asd-stan/ui-kit/assets/icons/close-icon-current-color.svg';
import { t } from 'i18next';
import { getStandardService } from 'src/standard/infrastructure/getters';
import { getStandardDetailsService } from 'src/standard/infrastructure/getters';



import { StageWorkflowStyled } from 'src/standard/components/single-standard/tabs-sections/single-standard-page-section/page-blocks/stages/stage-workflow.styled';


const EA_STAGES = [
	{ code: '00.20', name: 'NWP Assessment' },
	{ code: '00.50', name: 'NWP approved by WGC' },
	{ code: '20.10', name: 'Call for Participation' },
	{ code: '20.20', name: 'Committee Draft (CTD) started' },
	{ code: '20.99', name: 'Committee Draft (CTD) submitted' },
	{ code: '30.05', name: 'Commenting Phase Perform WG-level commenting' },
	{ code: '30.79', name: 'Comment Disposition/Comments resolved.' },
	{ code: '30.80', name: 'Consensus Draft/CD submitted' },
	{ code: '30.99', name: 'Approval for Publication' },
	{ code: '40.00', name: 'Publication/Final version' },
	{ code: '40.10', name: 'Corrigendum published' },
] as const;

const StagesWorkflow = () => {
	const params = useParams();
	const id = Number(params.id);

	const standardDetailsService = getStandardDetailsService();
	const standardService = getStandardService();
	const workflowRef = useRef<HTMLDivElement>(null);
	const hasAutoScrolledRef = useRef(false);
	const STAGE_STEP_OFFSET = 156;
	const standardId = standardDetailsService.standard?.id;

	useEffect(() => {
		if (!standardId) {
			return;
		}

		// Load full transaction history for a standard to know who set stage codes.
		standardService.getStandardTransactionHistory(standardId, 1000, 0);
	}, [standardId, standardService]);

	const transactions = useMemo(() => {
		const historyTransactions =
			standardService.standardTransactionHistory?.standardTransactionHistory;
		if (historyTransactions && historyTransactions.length > 0) {
			return historyTransactions;
		}

		const currentTransactionStages = standardDetailsService.standard?.transaction?.stages ?? [];
		if (currentTransactionStages.length > 0) {
			return [
				{
					id: -1,
					code: currentTransactionStages.map(stage => stage.code).join(', '),
					name: currentTransactionStages.map(stage => stage.name).join(', '),
					stageCodes: currentTransactionStages.map(stage => stage.code),
					createdBy: standardDetailsService.standard?.transaction?.createdBy ?? null,
					startDate: '',
					targetDate: null,
					completeDate: null,
					eaStageReason: null,
				},
			];
		}

		return [];
	}, [standardDetailsService.standard?.transaction, standardService.standardTransactionHistory]);

	const codeToCreators = useMemo(() => {
		return transactions.reduce<Record<string, Set<string>>>((acc, transaction) => {
			const creatorName = transaction.createdBy?.name;
			if (!creatorName) {
				return acc;
			}

			transaction.stageCodes.forEach(code => {
				if (!acc[code]) {
					acc[code] = new Set<string>();
				}
				acc[code].add(creatorName);
			});

			return acc;
		}, {});
	}, [transactions]);

	const activeCodes = useMemo(() => {
		const codes = transactions.flatMap(transaction => transaction.stageCodes);
		return new Set(codes);
	}, [transactions]);

	const hasRejectedInLastTransaction = useMemo(() => {
		if (transactions.length === 0) {
			return false;
		}

		const lastTransaction = [...transactions].sort((a, b) => a.id - b.id).at(-1);
		if (!lastTransaction) {
			return false;
		}

		const lastStageCode = lastTransaction.stageCodes.at(-1);
		return typeof lastStageCode === 'string' && lastStageCode.endsWith('.98');
	}, [transactions]);

	const stageList = useMemo(() => {
		const lastActiveStageCode = [...EA_STAGES].reverse().find(stage => activeCodes.has(stage.code))
			?.code;

		return EA_STAGES.map(stage => ({
			...stage,
			active: activeCodes.has(stage.code),
			isLastActive: stage.code === lastActiveStageCode,
			creators: Array.from(codeToCreators[stage.code] ?? []),
			rejected: stage.code === lastActiveStageCode && hasRejectedInLastTransaction,
		}));
	}, [activeCodes, codeToCreators, hasRejectedInLastTransaction]);

	useEffect(() => {
		if (hasAutoScrolledRef.current || !workflowRef.current) {
			return;
		}

		const lastActiveStage = [...stageList].reverse().find(stage => stage.active);
		if (!lastActiveStage) {
			return;
		}

		const stageIndex = stageList.findIndex(stage => stage.code === lastActiveStage.code);
		if (stageIndex < 1) {
			return;
		}

		const safeContainer = workflowRef.current;
		const targetLeft = STAGE_STEP_OFFSET * (stageIndex - 1);
		safeContainer.scrollTo({
			left: Math.max(targetLeft, 0),
			behavior: 'smooth',
		});

		hasAutoScrolledRef.current = true;
	}, [stageList]);

	return (
		<StageWorkflowStyled ref={workflowRef}>
			<div className="reils">
				<span className="dashed"></span>
				{stageList.map((stage, index: number) => (
					<div
						key={stage.code}
						data-code={stage.code}
						className={[
							'ea-stage-step',
							stage.active && !stage.rejected && 'active',
							stage.rejected && 'rejected',
							stage.isLastActive && 'lastActive',
							stage.active && stage.code === '40.00' && 'forceActive',
						]
							.filter(Boolean)
							.join(' ')}>
						<span className="ea-stage-step__index">
							{stage.rejected ? (
								<CloseIcon style={{ color: '#ffffff' }} />
							) :
								((stage.active && !stage.isLastActive) || (stage.active && stage.code === '40.00') )
									? (
								<Checkmark />
							) : (
								index + 1
							)}
						</span>
						<h4 className="ea-stage-step__title">{stage.name}</h4>
						<p className="ea-stage-step__responsible">
							<span>Responsible:</span>
							<br />
							{stage.creators.length > 0 ? stage.creators.join(', ') : '-'}
						</p>
						<Link className="ea-stage-step__more" to={`/standards/detailed/${id}/stageAndStatus`}>
							{t('standard.singleStandardPage.stagesWorkflow.moreInfo')}
						</Link>
					</div>
				))}
			</div>
		</StageWorkflowStyled>
	);
};

export default observer(StagesWorkflow);
